import React from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogContentText, 
    DialogActions,
    Button,
    Box,
    Typography,
    Avatar,
    Fade,
    Slide,
    useTheme,
    useMediaQuery,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    PriorityHigh as PriorityHighIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    CheckCircle as SuccessIcon,
    Close as CloseIcon
} from '@mui/icons-material';

// Styled components para design moderno
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        overflow: 'hidden',
        maxWidth: '500px', // Reduzido de 90vw para 500px
        width: '100%',
        maxHeight: '90vh',
        [theme.breakpoints.down('sm')]: {
            margin: 16,
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 12,
            maxHeight: 'calc(100vh - 32px)',
            '@supports (height: 100dvh)': { maxHeight: 'calc(100dvh - 32px)' },
            overflowY: 'auto',
        }
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme, iconType }) => {
    const getGradient = () => {
        switch (iconType) {
            case 'warning':
                return `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`;
            case 'error':
                return `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`;
            case 'success':
                return `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`;
            case 'info':
            default:
                return `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
        }
    };

    return {
        background: getGradient(),
        color: 'white',
        padding: '20px 32px 16px', // Reduzido padding vertical
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.1)',
            clipPath: 'polygon(0 0, 100% 0, 100% 30%, 0 70%)',
        },
        [theme.breakpoints.down('sm')]: {
            padding: '16px 24px 12px', // Reduzido padding para mobile
        }
    };
});

const StyledAvatar = styled(Avatar)(({ theme, iconType }) => {
    const getColor = () => {
        switch (iconType) {
            case 'warning':
                return theme.palette.warning.main;
            case 'error':
                return theme.palette.error.main;
            case 'success':
                return theme.palette.success.main;
            case 'info':
            default:
                return theme.palette.primary.main;
        }
    };

    return {
        width: 40, // Reduzido de 48 para 40
        height: 40, // Reduzido de 48 para 40
        backgroundColor: 'rgba(255,255,255,0.2)',
        color: 'white',
        border: '2px solid rgba(255,255,255,0.3)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        marginBottom: 12, // Reduzido de 16 para 12
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
        }
    };
});

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: '32px',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
        padding: '24px 20px',
    }
}));

// Novo componente para decoração do texto
const MessageContainer = styled(Box)(({ theme }) => ({
    marginTop: '10px',
    position: 'relative',
    padding: '28px 32px',
    borderRadius: 16,
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.grey[50], 0.6)} 100%)`,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    boxShadow: `
        0 4px 20px ${alpha(theme.palette.common.black, 0.08)},
        0 1px 3px ${alpha(theme.palette.common.black, 0.04)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.8)}
    `,
    backdropFilter: 'blur(10px)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-1px',
        left: '-1px',
        right: '-1px',
        bottom: '-1px',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
        borderRadius: 16,
        zIndex: -1,
        opacity: 0.3,
        transition: 'opacity 0.3s ease',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '12px',
        left: '12px',
        width: '4px',
        height: 'calc(100% - 24px)',
        background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 50%, ${theme.palette.primary.main} 100%)`,
        borderRadius: '2px',
        boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    '&:hover::before': {
        opacity: 0.4,
    },
    [theme.breakpoints.down('sm')]: {
        padding: '24px 20px',
        '&::after': {
            top: '10px',
            left: '10px',
            height: 'calc(100% - 20px)',
        }
    }
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: '20px 32px 32px',
    gap: 12,
    backgroundColor: alpha(theme.palette.grey[50], 0.5),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    [theme.breakpoints.down('sm')]: {
        padding: '16px 20px 24px',
        justifyContent: 'center',
        '& .MuiButton-root': {
            width: '100%',
            maxWidth: 200,
        }
    }
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem', // Aumentado de 0.875rem para 0.9rem
    padding: '12px 28px', // Aumentado padding para melhor proporção
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    letterSpacing: '0.02em', // Adicionado espaçamento entre letras
    lineHeight: 1.4, // Melhorado line-height
    fontFamily: theme.typography.fontFamily,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    },
    '&:active': {
        transform: 'translateY(0)',
    }
}));

const DialogInfoTitle = ({ title, iconType, subtitle }) => {
    const theme = useTheme(); // Adicionado hook useTheme
    
    const getIcon = () => {
        switch (iconType) {
            case 'warning':
                return <WarningIcon />;
            case 'error':
                return <ErrorIcon />;
            case 'success':
                return <SuccessIcon />;
            case 'info':
            default:
                return <InfoIcon />;
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            textAlign: 'center',
            position: 'relative',
            zIndex: 1
        }}>
            <StyledAvatar iconType={iconType}>
                {getIcon()}
            </StyledAvatar>
            <Typography 
                variant="h5" 
                component="h2"
                sx={{ 
                    fontWeight: 600, // Reduzido de 700 para 600
                    mb: subtitle ? 0.5 : 0, // Reduzido margin bottom
                    textShadow: '0 1px 3px rgba(0,0,0,0.15)', // Reduzido sombra
                    fontSize: { xs: '1.1rem', sm: '1.3rem' }, // Reduzido tamanho da fonte
                    letterSpacing: '-0.01em', // Reduzido espaçamento negativo
                    lineHeight: 1.3, // Ajustado line-height
                    fontFamily: theme.typography.fontFamily,
                    textTransform: 'none',
                }}
            >
                {title}
            </Typography>
            {subtitle && (
                <Typography 
                    variant="body2" 
                    sx={{ 
                        opacity: 0.85, // Reduzido opacity
                        fontWeight: 400,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' }, // Reduzido tamanho
                        letterSpacing: '0.02em',
                        lineHeight: 1.3,
                        fontFamily: theme.typography.fontFamily,
                        mt: 0.5, // Adicionado margin top pequeno
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function DialogInfo(props) {
    const theme = useTheme();
    // Abaixo de `sm` o modal vira bottom sheet (tema); tela cheia só em sm..md.
    const fullScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const getIconType = () => {
        if (props.iconType) return props.iconType;
        if (props.title?.toLowerCase().includes('erro') || props.title?.toLowerCase().includes('error')) return 'error';
        if (props.title?.toLowerCase().includes('sucesso') || props.title?.toLowerCase().includes('success')) return 'success';
        if (props.title?.toLowerCase().includes('aviso') || props.title?.toLowerCase().includes('warning')) return 'warning';
        return 'info';
    };

    const iconType = getIconType();

    return (
        <StyledDialog
            fullScreen={fullScreen}
            open={props.open}
            onClose={props.onClose}
            TransitionComponent={Transition}
            TransitionProps={{
                timeout: 300
            }}
            aria-labelledby="dialog-info-title"
            aria-describedby="dialog-info-description"
        >
            <StyledDialogTitle iconType={iconType}>
                <DialogInfoTitle 
                    title={props.title} 
                    iconType={iconType}
                    subtitle={props.subtitle}
                />
            </StyledDialogTitle>

            <StyledDialogContent>
                <Fade in timeout={400}>
                    <Box>
                        {props.content && (
                            <MessageContainer>
                                <DialogContentText
                                    id="dialog-info-description"
                                    sx={{
                                        marginTop: '10px', // Adicionado margem superior
                                        fontSize: '1rem',
                                        lineHeight: 1.8,
                                        color: theme.palette.text.primary,
                                        mb: props.children ? 3 : 0,
                                        textAlign: 'left',
                                        maxWidth: 450,
                                        mx: 'auto',
                                        fontWeight: 400,
                                        letterSpacing: '0.015em',
                                        fontFamily: theme.typography.fontFamily,
                                        position: 'relative',
                                        zIndex: 1,
                                        // Tipografia mais profissional
                                        '& strong': {
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                            letterSpacing: '0.02em',
                                        },
                                        '& em': {
                                            fontStyle: 'italic',
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                        },
                                        '& p': {
                                            marginBottom: '1rem',
                                            '&:last-child': {
                                                marginBottom: 0,
                                            }
                                        },
                                        '& ul, & ol': {
                                            paddingLeft: '1.5rem',
                                            marginBottom: '1rem',
                                            '& li': {
                                                marginBottom: '0.5rem',
                                                lineHeight: 1.6,
                                            }
                                        },
                                        '& blockquote': {
                                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                                            paddingLeft: '1rem',
                                            margin: '1rem 0',
                                            fontStyle: 'italic',
                                            color: theme.palette.text.secondary,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0 4px 4px 0',
                                        },
                                        '& code': {
                                            backgroundColor: alpha(theme.palette.grey[300], 0.3),
                                            padding: '0.2rem 0.4rem',
                                            borderRadius: '3px',
                                            fontSize: '0.9em',
                                            fontFamily: 'monospace',
                                            color: theme.palette.primary.main,
                                        }
                                    }}
                                >
                                    {props.content}
                                </DialogContentText>
                            </MessageContainer>
                        )}
                        
                        {props.children && (
                            <Box 
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                    mt: props.content ? 2 : 0
                                }}
                            >
                                {props.children}
                            </Box>
                        )}
                    </Box>
                </Fade>
            </StyledDialogContent>

            <StyledDialogActions>
                <StyledButton
                    variant="contained"
                    onClick={props.onClose}
                    startIcon={<CloseIcon />}
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        }
                    }}
                >
                    {props.closeButtonText || 'Fechar'}
                </StyledButton>
            </StyledDialogActions>
        </StyledDialog>
    );
}   